import React, { useState } from "react";

import axios from "axios";
import clsx from "clsx";

import CircularProgress from "@busbud/design-system-components/dist/CircularProgress/CircularProgress";
import { Button } from "@busbud/horizon";

import { useAppContext } from "@app/helpers/hooks";
import type { NapiUser } from "@app/types/napi-types";

import type { ConnectButtonProps } from "../props";

import { useStyles } from "./apple-connect-button.styles";
import { AppleLogo } from "./apple-logo";
import { toBusbudError } from "../../../../../helpers/api-error";
import { useScript } from "../../../../../helpers/use-script";
import {
  trackClickedConnectSocial,
  trackConnectSocialError
} from "../utils/tracking";

const PROVIDER = "apple";
const CLIENT_ID = "com.busbud";
const SDK_ERRORS: string[] = [];

export const AppleConnectButton: React.FC<ConnectButtonProps> = ({
  setInProgress,
  is_sign_in = true,
  onSuccess,
  onError,
  is_email_opt_in = false,
  loading = false,
  class_name,
  label
}) => {
  const { tracker, translator, geoData, user_country_code } = useAppContext();
  const { classes } = useStyles();
  const [is_sdk_ready, setSDKReady] = useState(false);

  useScript(
    "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
    () => {
      window.AppleID.auth.init({
        clientId: CLIENT_ID,
        scope: "name email",
        redirectURI: window.location.origin,
        usePopup: true
      });
      setSDKReady(true);
    }
  );

  async function handleClick() {
    if (!is_sdk_ready) {
      return;
    }
    setInProgress(true);

    const auto_enrol_new_user =
      geoData.allowsImplicitConsentForMarketingEmails(user_country_code);

    trackClickedConnectSocial(
      tracker,
      is_sign_in,
      PROVIDER,
      auto_enrol_new_user
    );

    try {
      const { authorization, user } = await window.AppleID.auth.signIn();

      const bb_user = await axios.post<NapiUser>("/auth/signin-apple", {
        id_token: authorization.id_token,
        first_name: user && user.name.firstName,
        last_name: user && user.name.lastName,
        email_opt_in: is_email_opt_in,
        auto_enrol_new_user
      });
      setInProgress(false);
      onSuccess(bb_user.data, PROVIDER);
    } catch (err) {
      setInProgress(false);
      trackConnectSocialError(tracker, PROVIDER, SDK_ERRORS, is_sign_in, err);
      onError(toBusbudError(err)!);
    }
  }

  return (
    <Button
      className={clsx(
        class_name,
        classes.button,
        "w-full bg-color-static-black-canvas text-color-static-white-canvas hover:text-color-static-white-canvas active:text-color-static-white-canvas disabled:bg-opacity-50"
      )}
      onClick={handleClick}
      variant="contained"
      size="lg"
      iconStart={
        loading ? (
          <CircularProgress size="sm" color="quaternary" />
        ) : (
          <AppleLogo />
        )
      }
    >
      {label || translator.t("!account.authentication.apple")}
    </Button>
  );
};
